import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  Briefcase,
  CheckCircle2,
  ClipboardList,
  Hash,
  Plus,
  Receipt,
  Save,
  Sparkles,
  Trash2,
  Wallet,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Field } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { useFamilyProfile } from "@/hooks/useProfile";
import {
  useBusinessYtd,
  useCarryforwards,
  useCurrentYearPersonal,
  useDeleteW2Stub,
  usePriorYearReturn,
  useTaxProjection,
  useUpdateBusinessYtd,
  useUpdateCarryforwards,
  useUpdateCurrentYearPersonal,
  useUpdatePriorYearReturn,
  useUpsertW2Stub,
  useW2Stubs,
  type BusinessYtd,
  type Carryforwards,
  type CurrentYearPersonal,
  type PriorYearReturn,
  type TaxProjection,
  type TaxProjectionMissingInput,
  type W2Person,
  type W2Stub,
} from "@/hooks/useTax";
import type { FilingStatus } from "@/hooks/useProfile";
import { formatMoney, formatPercent, todayIso } from "@/lib/utils";

const CURRENT_YEAR = new Date().getFullYear();
const PRIOR_YEAR = CURRENT_YEAR - 1;

/**
 * Anchors used both as scroll targets and as the routing destination the
 * engine references in MissingInput.routeHint (e.g. "/tax-intake#w2"). Keep
 * these in sync with TaxEngine.cs.
 */
const ANCHOR = {
  priorYear: "prior-year",
  currentYearPersonal: "current-year-personal",
  w2: "w2",
  business: "business",
  carryforwards: "carryforwards",
} as const;

/**
 * Map an engine-emitted MissingInput.fieldKey prefix to the card it belongs
 * to. Drives the per-card status pill ("Required", "Refine", "Complete",
 * "Optional") so users can scan the page and instantly see what to fill next.
 */
const CARD_FIELD_PREFIXES = {
  priorYear: ["PriorYear", "Estimated"],
  currentYearPersonal: ["Personal", "CurrentYear"],
  w2: ["W2"],
  business: ["Business", "Income.AnyIncome"],
  carryforwards: ["Carryforward"],
} as const;

type CardKey = keyof typeof CARD_FIELD_PREFIXES;

/** Status of a single intake card derived from the live projection. */
type CardStatus = "blocking" | "refine" | "complete" | "optional";

function cardStatusFromProjection(
  cardKey: CardKey,
  hasData: boolean,
  proj: TaxProjection | null,
): CardStatus {
  const prefixes = CARD_FIELD_PREFIXES[cardKey] as readonly string[];
  if (proj) {
    const matchingMissing = proj.missingInputs.filter((m) =>
      prefixes.some((p) => m.fieldKey.startsWith(p)),
    );
    if (matchingMissing.some((m) => m.isBlocking)) return "blocking";
    if (matchingMissing.length > 0) return "refine";
  }
  if (hasData) return "complete";
  return "optional";
}

const CARD_STATUS_BADGE: Record<CardStatus, { label: string; tone: "danger" | "warning" | "success" | "neutral" }> = {
  blocking: { label: "Required", tone: "danger" },
  refine: { label: "Refine", tone: "warning" },
  complete: { label: "Saved", tone: "success" },
  optional: { label: "Optional", tone: "neutral" },
};

/**
 * Single intake page. Each card maps to one server-side intake aggregate.
 * The engine status card on top reflects the live projection so the user
 * can see exactly which inputs unblock the projection as they save.
 */
export function TaxIntakePage() {
  const projection = useTaxProjection(CURRENT_YEAR);
  const proj = projection.data?.projection ?? null;
  const family = useFamilyProfile();

  // Smart anchor scroll: when the URL has a hash, scroll to the matching card.
  // We do this on mount AND when the projection loads (because the cards may
  // skeleton-mount before scroll-to-fragment.)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [projection.isLoading]);

  const blocked = proj?.hasBlockingGaps === true;

  return (
    <>
      <PageHeader
        eyebrow="Tax Intake"
        title="Hard facts in, real projection out"
        description="The tax engine refuses to invent numbers. Fill these sections from your actual paystubs, prior return, and bookkeeping. Save any card to instantly re-project."
      />

      <HowThisWorksCard blocked={blocked} />

      <EngineStatusCard
        loading={projection.isLoading}
        proj={proj}
        marginalBusiness={projection.data?.marginalBusinessLiability ?? null}
      />

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <PriorYearCard year={PRIOR_YEAR} proj={proj} familyProfile={family.data ?? null} />
        <CurrentYearPersonalCard year={CURRENT_YEAR} proj={proj} />
      </div>

      <div className="mt-6">
        <W2StubsCard year={CURRENT_YEAR} proj={proj} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <BusinessYtdCard year={CURRENT_YEAR} proj={proj} />
        <CarryforwardsCard year={CURRENT_YEAR} proj={proj} />
      </div>
    </>
  );
}

// =============================================================================
// "How this works" — first-time orienter, collapsed once user has data
// =============================================================================

function HowThisWorksCard({ blocked }: { blocked: boolean }) {
  if (!blocked) {
    return (
      <Card className="mt-2 border-emerald-500/40 bg-emerald-500/5">
        <CardContent className="flex flex-wrap items-center gap-3 py-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-300" />
          <span className="font-medium text-emerald-200">Tax engine is unblocked.</span>
          <span className="text-muted-foreground">
            Refine any section below to sharpen the projection — every save re-projects in
            real time.
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-2 border-amber-500/50 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-amber-300" />
          How this works
        </CardTitle>
        <CardDescription>
          Fill these in any order. The engine status above always shows what&apos;s still
          blocking the projection.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
        <Step
          n={1}
          title="Last year&apos;s 1040 + WI Form 1"
          why="Drives safe-harbor estimated-tax math so we know exactly how much to set aside each quarter."
          anchor={ANCHOR.priorYear}
        />
        <Step
          n={2}
          title="W-2 stubs (owner + spouse)"
          why="YTD Social-Security wages tell the engine how much SE-tax SS base is already used by payroll."
          anchor={ANCHOR.w2}
        />
        <Step
          n={3}
          title="Business YTD"
          why="Gross receipts + expenses produce Schedule C net profit, which flows to SE tax + QBI."
          anchor={ANCHOR.business}
        />
      </CardContent>
    </Card>
  );
}

function Step({ n, title, why, anchor }: { n: number; title: string; why: string; anchor: string }) {
  return (
    <a
      href={`#${anchor}`}
      className="group rounded-md border border-border/40 bg-background/40 p-3 transition-colors hover:border-primary/60 hover:bg-primary/5"
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary">
          {n}
        </span>
        <span className="font-medium group-hover:text-foreground">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground">{why}</p>
    </a>
  );
}

// =============================================================================
// Engine status — live mirror of /api/tax/projection
// =============================================================================

function EngineStatusCard({
  loading,
  proj,
  marginalBusiness,
}: {
  loading: boolean;
  proj: TaxProjection | null;
  marginalBusiness: number | null;
}) {
  if (loading) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Engine status</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!proj) {
    return (
      <Card className="mt-4 border-destructive/50">
        <CardContent className="pt-6 text-sm text-destructive">
          Could not load tax projection. Make sure the API is running.
        </CardContent>
      </Card>
    );
  }

  const blocked = proj.hasBlockingGaps;

  return (
    <Card
      className={
        "mt-4 " + (blocked ? "border-amber-500/60 bg-amber-500/5" : "border-emerald-500/40")
      }
    >
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-sm">
              <ClipboardList className="h-4 w-4" />
              Engine status — {proj.taxYear} projection
            </CardTitle>
            <CardDescription>
              {blocked
                ? "Add the items below and the projection lights up immediately."
                : "Projection is live — every cell here is computed from the cards on this page."}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {blocked ? <Badge tone="warning">Blocked</Badge> : <Badge tone="success">Computed</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <Stat label="Total liability" value={blocked ? "—" : formatMoney(proj.totalLiability)} />
        <Stat
          label="Marginal LLC slice"
          value={blocked || marginalBusiness == null ? "—" : formatMoney(marginalBusiness)}
        />
        <Stat
          label="Marginal next-$ rate"
          value={blocked ? "—" : formatPercent(proj.nextDollarBlendedRate)}
        />
        <Stat
          label="Effective rate (AGI)"
          value={blocked ? "—" : formatPercent(proj.effectiveTaxRateOnAgi)}
        />

        {proj.missingInputs.length > 0 && (
          <div className="col-span-2 mt-2 md:col-span-4">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {blocked ? "Blocking inputs" : "Refinements (non-blocking)"}
            </div>
            <ul className="space-y-1">
              {proj.missingInputs.map((m) => (
                <MissingInputRow key={m.fieldKey} m={m} />
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MissingInputRow({ m }: { m: TaxProjectionMissingInput }) {
  // routeHint may be a same-page anchor like "/tax-intake#w2" — use a plain <a>
  // so the browser handles fragment scrolling. Otherwise use TanStack Link.
  const isHashOnSamePage = m.routeHint?.startsWith("/tax-intake#") === true;
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-border/40 bg-background/40 p-2 text-xs">
      <div>
        <span className={m.isBlocking ? "font-semibold text-foreground" : ""}>{m.label}</span>
        <span className="ml-1 text-muted-foreground">— {m.why}</span>
      </div>
      {m.routeHint &&
        (isHashOnSamePage ? (
          <a href={m.routeHint.replace("/tax-intake", "")} className="shrink-0">
            <Button size="sm" variant="ghost">
              Open
            </Button>
          </a>
        ) : (
          <Link to={m.routeHint} className="shrink-0">
            <Button size="sm" variant="ghost">
              Open
            </Button>
          </Link>
        ))}
    </li>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/50 bg-background/40 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

// =============================================================================
// Reusable money input — null-aware (empty string → null on save)
// =============================================================================

function MoneyInput({
  value,
  onChange,
  placeholder,
  step = "1",
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  step?: string;
}) {
  return (
    <Input
      type="number"
      inputMode="decimal"
      step={step}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    />
  );
}

// =============================================================================
// Card header w/ status pill + saved-flash
// =============================================================================

function CardTitleRow({
  icon,
  title,
  description,
  status,
  savedAt,
  saving,
  onSave,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  status: CardStatus;
  savedAt: number | null;
  saving: boolean;
  onSave: () => void;
}) {
  const badge = CARD_STATUS_BADGE[status];
  const showSaved = savedAt && Date.now() - savedAt < 2500;
  return (
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
            {icon}
            {title}
            <Badge tone={badge.tone}>{badge.label}</Badge>
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showSaved && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          <Button disabled={saving} onClick={onSave}>
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </CardHeader>
  );
}

function useSavedFlash(isSuccess: boolean) {
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const lastFlag = useRef(false);
  useEffect(() => {
    if (isSuccess && !lastFlag.current) setSavedAt(Date.now());
    lastFlag.current = isSuccess;
  }, [isSuccess]);
  return savedAt;
}

// =============================================================================
// Prior-Year Return card
// =============================================================================

function PriorYearCard({
  year,
  proj,
  familyProfile,
}: {
  year: number;
  proj: TaxProjection | null;
  familyProfile: { filingStatus: FilingStatus | null; stateOfResidence: string | null } | null;
}) {
  const q = usePriorYearReturn(year);
  const update = useUpdatePriorYearReturn(year);
  const [draft, setDraft] = useState<PriorYearReturn | null>(null);
  const savedAt = useSavedFlash(update.isSuccess);

  // Smart prefill: if the prior-year row exists but has no filing status / state
  // yet, default them from FamilyProfile so the user doesn't have to re-enter
  // facts already given on the People page.
  useEffect(() => {
    if (q.data && !draft) {
      const seed = { ...q.data };
      if (!seed.filingStatus && familyProfile?.filingStatus) {
        seed.filingStatus = familyProfile.filingStatus;
      }
      if ((!seed.stateCode || seed.stateCode === "") && familyProfile?.stateOfResidence) {
        seed.stateCode = familyProfile.stateOfResidence;
      }
      setDraft(seed);
    }
  }, [q.data, draft, familyProfile]);

  const hasData = useMemo(
    () => q.data != null && (q.data.line11Agi != null || q.data.line24TotalTax != null),
    [q.data],
  );
  const status = cardStatusFromProjection("priorYear", hasData, proj);

  if (q.isLoading || !draft) {
    return (
      <Card id={ANCHOR.priorYear}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Receipt className="h-4 w-4 text-primary" />
            Prior-year return — Form 1040 ({year})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id={ANCHOR.priorYear}>
      <CardTitleRow
        icon={<Receipt className="h-4 w-4 text-primary" />}
        title={`Prior-year return — Form 1040 (${year})`}
        description={`Punch in the line numbers from your filed ${year} 1040 + WI Form 1. Drives safe-harbor estimated-tax math.`}
        status={status}
        savedAt={savedAt}
        saving={update.isPending}
        onSave={() => update.mutate(draft)}
      />
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Filing status">
          <Select
            value={draft.filingStatus}
            onChange={(e) =>
              setDraft({ ...draft, filingStatus: e.target.value as FilingStatus })
            }
          >
            <option value="Single">Single</option>
            <option value="MarriedFilingJointly">MFJ</option>
            <option value="MarriedFilingSeparately">MFS</option>
            <option value="HeadOfHousehold">HoH</option>
            <option value="QualifyingSurvivingSpouse">QSS</option>
          </Select>
        </Field>
        <Field label="Line 1a — wages">
          <MoneyInput
            value={draft.line1aWages}
            onChange={(v) => setDraft({ ...draft, line1aWages: v })}
          />
        </Field>
        <Field label="Line 11 — AGI">
          <MoneyInput value={draft.line11Agi} onChange={(v) => setDraft({ ...draft, line11Agi: v })} />
        </Field>
        <Field label="Line 15 — taxable income">
          <MoneyInput
            value={draft.line15TaxableIncome}
            onChange={(v) => setDraft({ ...draft, line15TaxableIncome: v })}
          />
        </Field>
        <Field label="Line 16 — tax">
          <MoneyInput value={draft.line16Tax} onChange={(v) => setDraft({ ...draft, line16Tax: v })} />
        </Field>
        <Field label="Line 24 — total tax" hint="Drives safe-harbor multiplier.">
          <MoneyInput
            value={draft.line24TotalTax}
            onChange={(v) => setDraft({ ...draft, line24TotalTax: v })}
          />
        </Field>
        <Field label="Line 25 — withholding">
          <MoneyInput
            value={draft.line25Withholding}
            onChange={(v) => setDraft({ ...draft, line25Withholding: v })}
          />
        </Field>
        <Field label="Line 26 — estimated payments">
          <MoneyInput
            value={draft.line26EstimatedPayments}
            onChange={(v) => setDraft({ ...draft, line26EstimatedPayments: v })}
          />
        </Field>
        <Field label="Sch C net profit">
          <MoneyInput
            value={draft.scheduleCNetProfit}
            onChange={(v) => setDraft({ ...draft, scheduleCNetProfit: v })}
          />
        </Field>
        <Field label="Sch SE — total SE tax">
          <MoneyInput
            value={draft.scheduleSeTax}
            onChange={(v) => setDraft({ ...draft, scheduleSeTax: v })}
          />
        </Field>
        <Field label="WI Form 1 — total tax">
          <MoneyInput
            value={draft.stateTotalTax}
            onChange={(v) => setDraft({ ...draft, stateTotalTax: v })}
          />
        </Field>
        <Field label="WI Form 1 — withholding">
          <MoneyInput
            value={draft.stateWithholding}
            onChange={(v) => setDraft({ ...draft, stateWithholding: v })}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Current-Year Personal card
// =============================================================================

function CurrentYearPersonalCard({ year, proj }: { year: number; proj: TaxProjection | null }) {
  const q = useCurrentYearPersonal(year);
  const update = useUpdateCurrentYearPersonal(year);
  const [draft, setDraft] = useState<CurrentYearPersonal | null>(null);
  const savedAt = useSavedFlash(update.isSuccess);

  useEffect(() => {
    if (q.data && !draft) setDraft(q.data);
  }, [q.data, draft]);

  const hasData = useMemo(
    () =>
      q.data != null &&
      [
        q.data.ytdTaxableInterest,
        q.data.ytdOrdinaryDividends,
        q.data.ytdLongTermCapitalGain,
        q.data.ytdHsaContribution,
        q.data.ytdFederalEstimatedPaid,
      ].some((v) => v != null),
    [q.data],
  );
  const status = cardStatusFromProjection("currentYearPersonal", hasData, proj);

  if (q.isLoading || !draft) {
    return (
      <Card id={ANCHOR.currentYearPersonal}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Wallet className="h-4 w-4 text-primary" />
            Current-year personal income ({year} YTD)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id={ANCHOR.currentYearPersonal}>
      <CardTitleRow
        icon={<Wallet className="h-4 w-4 text-primary" />}
        title={`Current-year personal income (${year} YTD)`}
        description="Interest, dividends, capital gains, HSA / IRA contributions, estimated payments. Optional projected-remaining overrides if you have specific numbers in mind."
        status={status}
        savedAt={savedAt}
        saving={update.isPending}
        onSave={() => update.mutate(draft)}
      />
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="YTD taxable interest">
          <MoneyInput
            value={draft.ytdTaxableInterest}
            onChange={(v) => setDraft({ ...draft, ytdTaxableInterest: v })}
          />
        </Field>
        <Field label="YTD ordinary dividends">
          <MoneyInput
            value={draft.ytdOrdinaryDividends}
            onChange={(v) => setDraft({ ...draft, ytdOrdinaryDividends: v })}
          />
        </Field>
        <Field label="YTD qualified dividends" hint="Subset of ordinary; LTCG-rated.">
          <MoneyInput
            value={draft.ytdQualifiedDividends}
            onChange={(v) => setDraft({ ...draft, ytdQualifiedDividends: v })}
          />
        </Field>
        <Field label="YTD short-term capital gain">
          <MoneyInput
            value={draft.ytdShortTermCapitalGain}
            onChange={(v) => setDraft({ ...draft, ytdShortTermCapitalGain: v })}
          />
        </Field>
        <Field label="YTD long-term capital gain">
          <MoneyInput
            value={draft.ytdLongTermCapitalGain}
            onChange={(v) => setDraft({ ...draft, ytdLongTermCapitalGain: v })}
          />
        </Field>
        <Field label="YTD passive K-1 income">
          <MoneyInput
            value={draft.ytdPassiveK1Income}
            onChange={(v) => setDraft({ ...draft, ytdPassiveK1Income: v })}
          />
        </Field>
        <Field label="YTD HSA contribution (personal)">
          <MoneyInput
            value={draft.ytdHsaContribution}
            onChange={(v) => setDraft({ ...draft, ytdHsaContribution: v })}
          />
        </Field>
        <Field label="YTD traditional IRA contribution">
          <MoneyInput
            value={draft.ytdTraditionalIraContribution}
            onChange={(v) => setDraft({ ...draft, ytdTraditionalIraContribution: v })}
          />
        </Field>
        <Field label="YTD federal estimated paid (1040-ES)">
          <MoneyInput
            value={draft.ytdFederalEstimatedPaid}
            onChange={(v) => setDraft({ ...draft, ytdFederalEstimatedPaid: v })}
          />
        </Field>
        <Field label="YTD WI estimated paid (Form 1-ES)">
          <MoneyInput
            value={draft.ytdStateEstimatedPaid}
            onChange={(v) => setDraft({ ...draft, ytdStateEstimatedPaid: v })}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// W-2 stubs — multi-row per-employer per-person
// =============================================================================

function W2StubsCard({ year, proj }: { year: number; proj: TaxProjection | null }) {
  const stubs = useW2Stubs(year);
  const upsert = useUpsertW2Stub(year);
  const remove = useDeleteW2Stub(year);
  const savedAt = useSavedFlash(upsert.isSuccess || remove.isSuccess);
  const [editing, setEditing] = useState<W2Stub | null>(null);

  const hasData = (stubs.data?.length ?? 0) > 0;
  const status = cardStatusFromProjection("w2", hasData, proj);
  const badge = CARD_STATUS_BADGE[status];

  const startNew = () => {
    setEditing({
      id: crypto.randomUUID(),
      ownerUserId: "",
      taxYear: year,
      personRole: "Owner",
      employerName: "",
      employerEin: null,
      asOfDate: todayIso(),
      ytdGrossWages: null,
      ytdFederalWithholding: null,
      ytdSocialSecurityWages: null,
      ytdSocialSecurityTaxWithheld: null,
      ytdMedicareWages: null,
      ytdMedicareTaxWithheld: null,
      ytdStateWithholding: null,
      projectedAnnualGrossWages: null,
      projectedAnnualFederalWithholding: null,
      projectedAnnualStateWithholding: null,
      ytdEmployee401kPretax: null,
      ytdSection125Pretax: null,
      notes: "",
    });
  };

  return (
    <Card id={ANCHOR.w2}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
              <Briefcase className="h-4 w-4 text-primary" />
              W-2 stubs ({year}) — owner + spouse, per employer
              <Badge tone={badge.tone}>{badge.label}</Badge>
            </CardTitle>
            <CardDescription>
              YTD wages drive Social-Security wage-base usage for SE tax. Add one row per active
              employer.
            </CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {savedAt && Date.now() - savedAt < 2500 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            <Button onClick={startNew} disabled={editing !== null}>
              <Plus className="h-4 w-4" />
              Add W-2
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {stubs.isLoading && <Skeleton className="h-24 w-full" />}
        {!stubs.isLoading && (stubs.data?.length ?? 0) === 0 && !editing && (
          <p className="text-sm text-muted-foreground">
            No W-2s entered yet. Add one for every active employer in {year}.
          </p>
        )}
        {(stubs.data ?? []).map((s) => (
          <W2Row
            key={s.id}
            stub={s}
            onEdit={() => setEditing(s)}
            onDelete={() => remove.mutate(s.id)}
          />
        ))}
        {editing && (
          <W2Editor
            initial={editing}
            saving={upsert.isPending}
            onCancel={() => setEditing(null)}
            onSave={async (s) => {
              await upsert.mutateAsync(s);
              setEditing(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function W2Row({
  stub,
  onEdit,
  onDelete,
}: {
  stub: W2Stub;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-background/40 p-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={stub.personRole === "Owner" ? "info" : "neutral"}>{stub.personRole}</Badge>
          <span className="font-medium">{stub.employerName || "(unnamed employer)"}</span>
          {stub.asOfDate && (
            <span className="text-xs text-muted-foreground">as of {stub.asOfDate}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          <span>YTD: {formatMoney(stub.ytdGrossWages ?? 0)}</span>
          <span>Projected: {formatMoney(stub.projectedAnnualGrossWages ?? null)}</span>
          <span>SS wages YTD: {formatMoney(stub.ytdSocialSecurityWages ?? 0)}</span>
          <span>Fed W/H YTD: {formatMoney(stub.ytdFederalWithholding ?? 0)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button size="icon" variant="ghost" onClick={onDelete} aria-label="Delete">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

function W2Editor({
  initial,
  saving,
  onCancel,
  onSave,
}: {
  initial: W2Stub;
  saving: boolean;
  onCancel: () => void;
  onSave: (s: W2Stub) => void;
}) {
  const [draft, setDraft] = useState<W2Stub>(initial);
  return (
    <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Person">
          <Select
            value={draft.personRole}
            onChange={(e) => setDraft({ ...draft, personRole: e.target.value as W2Person })}
          >
            <option value="Owner">Owner</option>
            <option value="Spouse">Spouse</option>
          </Select>
        </Field>
        <Field label="Employer name" className="sm:col-span-2">
          <Input
            value={draft.employerName}
            onChange={(e) => setDraft({ ...draft, employerName: e.target.value })}
            placeholder="e.g. Acme Corp"
          />
        </Field>
        <Field label="As-of date (YTD basis)">
          <Input
            type="date"
            value={draft.asOfDate ?? ""}
            onChange={(e) => setDraft({ ...draft, asOfDate: e.target.value || null })}
          />
        </Field>
        <Field label="Employer EIN">
          <Input
            value={draft.employerEin ?? ""}
            onChange={(e) => setDraft({ ...draft, employerEin: e.target.value || null })}
            placeholder="12-3456789"
          />
        </Field>
        <Field label="YTD gross wages (Box 1)">
          <MoneyInput
            value={draft.ytdGrossWages}
            onChange={(v) => setDraft({ ...draft, ytdGrossWages: v })}
          />
        </Field>
        <Field label="YTD federal W/H (Box 2)">
          <MoneyInput
            value={draft.ytdFederalWithholding}
            onChange={(v) => setDraft({ ...draft, ytdFederalWithholding: v })}
          />
        </Field>
        <Field label="YTD SS wages (Box 3)" hint="Drives SE-tax SS-base remaining.">
          <MoneyInput
            value={draft.ytdSocialSecurityWages}
            onChange={(v) => setDraft({ ...draft, ytdSocialSecurityWages: v })}
          />
        </Field>
        <Field label="YTD Medicare wages (Box 5)">
          <MoneyInput
            value={draft.ytdMedicareWages}
            onChange={(v) => setDraft({ ...draft, ytdMedicareWages: v })}
          />
        </Field>
        <Field label="YTD state W/H (Box 17)">
          <MoneyInput
            value={draft.ytdStateWithholding}
            onChange={(v) => setDraft({ ...draft, ytdStateWithholding: v })}
          />
        </Field>
        <Field label="Projected ANNUAL gross" hint="Optional override; otherwise we annualize YTD.">
          <MoneyInput
            value={draft.projectedAnnualGrossWages}
            onChange={(v) => setDraft({ ...draft, projectedAnnualGrossWages: v })}
          />
        </Field>
        <Field label="Projected ANNUAL fed W/H">
          <MoneyInput
            value={draft.projectedAnnualFederalWithholding}
            onChange={(v) => setDraft({ ...draft, projectedAnnualFederalWithholding: v })}
          />
        </Field>
        <Field label="Projected ANNUAL state W/H">
          <MoneyInput
            value={draft.projectedAnnualStateWithholding}
            onChange={(v) => setDraft({ ...draft, projectedAnnualStateWithholding: v })}
          />
        </Field>
        <Field label="YTD pre-tax 401(k)">
          <MoneyInput
            value={draft.ytdEmployee401kPretax}
            onChange={(v) => setDraft({ ...draft, ytdEmployee401kPretax: v })}
          />
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => onSave(draft)} disabled={saving}>
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save W-2"}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Business YTD card
// =============================================================================

function BusinessYtdCard({ year, proj }: { year: number; proj: TaxProjection | null }) {
  const q = useBusinessYtd(year);
  const update = useUpdateBusinessYtd(year);
  const [draft, setDraft] = useState<BusinessYtd | null>(null);
  const savedAt = useSavedFlash(update.isSuccess);

  useEffect(() => {
    if (q.data && !draft) setDraft(q.data);
  }, [q.data, draft]);

  const hasData = useMemo(
    () =>
      q.data != null &&
      (q.data.ytdGrossReceipts != null || q.data.projectedAnnualGrossReceipts != null),
    [q.data],
  );
  const status = cardStatusFromProjection("business", hasData, proj);

  if (q.isLoading || !draft) {
    return (
      <Card id={ANCHOR.business}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Briefcase className="h-4 w-4 text-primary" />
            Business YTD ({year})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id={ANCHOR.business}>
      <CardTitleRow
        icon={<Briefcase className="h-4 w-4 text-primary" />}
        title={`Business YTD (${year})`}
        description="Schedule C totals + retirement/HSA contributions through the business + S-Corp wages."
        status={status}
        savedAt={savedAt}
        saving={update.isPending}
        onSave={() => update.mutate(draft)}
      />
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="As-of date">
          <Input
            type="date"
            value={draft.asOfDate ?? ""}
            onChange={(e) => setDraft({ ...draft, asOfDate: e.target.value || null })}
          />
        </Field>
        <Field label="Is SSTB?" hint="Software consulting / professional services = yes.">
          <Select
            value={draft.isSstb ? "yes" : "no"}
            onChange={(e) => setDraft({ ...draft, isSstb: e.target.value === "yes" })}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </Field>
        <Field label="YTD gross receipts">
          <MoneyInput
            value={draft.ytdGrossReceipts}
            onChange={(v) => setDraft({ ...draft, ytdGrossReceipts: v })}
          />
        </Field>
        <Field label="YTD ordinary expenses">
          <MoneyInput
            value={draft.ytdOrdinaryExpenses}
            onChange={(v) => setDraft({ ...draft, ytdOrdinaryExpenses: v })}
          />
        </Field>
        <Field label="YTD depreciation (incl. §179)">
          <MoneyInput
            value={draft.ytdDepreciation}
            onChange={(v) => setDraft({ ...draft, ytdDepreciation: v })}
          />
        </Field>
        <Field label="YTD home-office (Form 8829)">
          <MoneyInput
            value={draft.ytdHomeOfficeDeduction}
            onChange={(v) => setDraft({ ...draft, ytdHomeOfficeDeduction: v })}
          />
        </Field>
        <Field
          label="Projected ANNUAL gross receipts"
          hint="Optional override; otherwise we annualize YTD."
        >
          <MoneyInput
            value={draft.projectedAnnualGrossReceipts}
            onChange={(v) => setDraft({ ...draft, projectedAnnualGrossReceipts: v })}
          />
        </Field>
        <Field label="Projected ANNUAL ordinary expenses">
          <MoneyInput
            value={draft.projectedAnnualOrdinaryExpenses}
            onChange={(v) => setDraft({ ...draft, projectedAnnualOrdinaryExpenses: v })}
          />
        </Field>
        <Field label="YTD S-Corp owner wages">
          <MoneyInput
            value={draft.ytdSCorpOwnerWages}
            onChange={(v) => setDraft({ ...draft, ytdSCorpOwnerWages: v })}
          />
        </Field>
        <Field label="Projected ANNUAL S-Corp owner wages">
          <MoneyInput
            value={draft.projectedAnnualSCorpOwnerWages}
            onChange={(v) => setDraft({ ...draft, projectedAnnualSCorpOwnerWages: v })}
          />
        </Field>
        <Field label="YTD Solo 401(k) employee deferral">
          <MoneyInput
            value={draft.ytdSolo401kEmployeeDeferral}
            onChange={(v) => setDraft({ ...draft, ytdSolo401kEmployeeDeferral: v })}
          />
        </Field>
        <Field label="YTD Solo 401(k) employer profit-share">
          <MoneyInput
            value={draft.ytdSolo401kEmployerProfitShare}
            onChange={(v) => setDraft({ ...draft, ytdSolo401kEmployerProfitShare: v })}
          />
        </Field>
        <Field label="YTD business HSA contribution">
          <MoneyInput
            value={draft.ytdBusinessHsaContribution}
            onChange={(v) => setDraft({ ...draft, ytdBusinessHsaContribution: v })}
          />
        </Field>
        <Field label="YTD self-employed health insurance">
          <MoneyInput
            value={draft.ytdSelfEmployedHealthInsurance}
            onChange={(v) => setDraft({ ...draft, ytdSelfEmployedHealthInsurance: v })}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Carryforwards card
// =============================================================================

function CarryforwardsCard({ year, proj }: { year: number; proj: TaxProjection | null }) {
  const q = useCarryforwards(year);
  const update = useUpdateCarryforwards(year);
  const [draft, setDraft] = useState<Carryforwards | null>(null);
  const savedAt = useSavedFlash(update.isSuccess);

  useEffect(() => {
    if (q.data && !draft) setDraft(q.data);
  }, [q.data, draft]);

  const hasData = useMemo(
    () =>
      q.data != null &&
      [
        q.data.federalNolCarryforward,
        q.data.longTermCapitalLossCarryforward,
        q.data.shortTermCapitalLossCarryforward,
        q.data.section179Disallowed,
        q.data.passiveActivityLossCarryforward,
        q.data.amtCreditCarryforward,
      ].some((v) => v != null && v !== 0),
    [q.data],
  );
  const status = cardStatusFromProjection("carryforwards", hasData, proj);

  if (q.isLoading || !draft) {
    return (
      <Card id={ANCHOR.carryforwards}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Hash className="h-4 w-4 text-primary" />
            Carryforwards ({year})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id={ANCHOR.carryforwards}>
      <CardTitleRow
        icon={<Hash className="h-4 w-4 text-primary" />}
        title={`Carryforwards (${year})`}
        description={`Brought forward from ${year - 1}: NOLs, capital losses, §179 disallowed, AMT credit.`}
        status={status}
        savedAt={savedAt}
        saving={update.isPending}
        onSave={() => update.mutate(draft)}
      />
      <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Federal NOL carryforward">
          <MoneyInput
            value={draft.federalNolCarryforward}
            onChange={(v) => setDraft({ ...draft, federalNolCarryforward: v })}
          />
        </Field>
        <Field label="WI NOL carryforward">
          <MoneyInput
            value={draft.stateNolCarryforward}
            onChange={(v) => setDraft({ ...draft, stateNolCarryforward: v })}
          />
        </Field>
        <Field label="LT capital loss carryforward">
          <MoneyInput
            value={draft.longTermCapitalLossCarryforward}
            onChange={(v) => setDraft({ ...draft, longTermCapitalLossCarryforward: v })}
          />
        </Field>
        <Field label="ST capital loss carryforward">
          <MoneyInput
            value={draft.shortTermCapitalLossCarryforward}
            onChange={(v) => setDraft({ ...draft, shortTermCapitalLossCarryforward: v })}
          />
        </Field>
        <Field label="WI capital loss carryforward">
          <MoneyInput
            value={draft.stateCapitalLossCarryforward}
            onChange={(v) => setDraft({ ...draft, stateCapitalLossCarryforward: v })}
          />
        </Field>
        <Field label="§179 disallowed (federal)">
          <MoneyInput
            value={draft.section179Disallowed}
            onChange={(v) => setDraft({ ...draft, section179Disallowed: v })}
          />
        </Field>
        <Field label="Passive activity loss carryforward">
          <MoneyInput
            value={draft.passiveActivityLossCarryforward}
            onChange={(v) => setDraft({ ...draft, passiveActivityLossCarryforward: v })}
          />
        </Field>
        <Field label="AMT credit carryforward">
          <MoneyInput
            value={draft.amtCreditCarryforward}
            onChange={(v) => setDraft({ ...draft, amtCreditCarryforward: v })}
          />
        </Field>
      </CardContent>
    </Card>
  );
}
